import CTNButton from '@ctn/ui/components/ctn-button';
import CTNDropdownButton from '@ctn/ui/components/ctn-dropdown-button';
import CTNEllipsis from '@ctn/ui/components/ctn-ellipsis';

import {
	type ISubsanacion,
	type ISubsanacionDocumento,
	type ISubsanacionSujeto
} from '@/interfaces/model/don.interface';
import type { CustomColumnDef } from '@ctn/ui/types/ctn-table';

type RowData = ISubsanacionDocumento | ISubsanacionSujeto;

const setColumn = <K extends keyof ISubsanacionDocumento | keyof ISubsanacionSujeto>(
	key: K,
	header: string,
	width?: number,
	customCell?: CustomColumnDef<RowData>['cell']
): CustomColumnDef<RowData> => ({
	accessorKey: key as string,
	header,
	enableSorting: false,
	...(width ? { th: { width } } : {}),
	responsive: { sizeColumn: 'full' },
	cell: customCell ?? (({ getValue }) => <CTNEllipsis content={getValue() as string} />),
});

const getBaseColumns = (): Array<CustomColumnDef<RowData>> => [
	setColumn('fechaCreacion', 'Fecha alta', 164),
	setColumn('tipo', 'Tipo', 140, ({ getValue }) => {
		const { descripcion } = getValue() as ISubsanacion['tipo'];
		return <CTNEllipsis content={descripcion} />;
	}),
	setColumn('tramite', 'Nº solicitud', 164, ({ getValue }) => {
		const { numeroTramite } = getValue() as ISubsanacion['tramite'];
		return <CTNEllipsis content={numeroTramite} />;
	}),
];

const getColumnsPendiente = ({
	onSubsanar,
	onViewDetail,
}: {
	onSubsanar: (value: RowData) => void;
	onViewDetail: (value: RowData) => void;
}): Array<CustomColumnDef<RowData>> => [
	...getBaseColumns(),
	setColumn('observacionesPeticion', 'Motivo de subsanación'),
	{
		accessorKey: 'actions',
		header: '',
		enableSorting: false,
		th: { width: 48 },
		td: { align: 'center' },
		responsive: { sizeColumn: 'full' },
		cell: ({ row }) => (
			<CTNDropdownButton
				variant="ghost"
				mode="secondary"
				size="size-32"
				label="Acciones"
				icon={[{ icon: 'moreVertical', color: 'none', position: 'left' }]}
				items={[
					{ label: 'Ver detalle', onClick: () => onViewDetail(row.original) },
					{ label: 'Subsanar', onClick: () => onSubsanar(row.original) },
				]}
			/>
		),
	},
];

const getColumnsAtendidas = ({
	onViewDetail,
}: {
	onViewDetail: (value: RowData) => void;
}): Array<CustomColumnDef<RowData>> => [
	...getBaseColumns(),
	setColumn('fechaSubsanacion', 'Fecha subsanación', 164),
	setColumn('observacionesPeticion', 'Motivo de subsanación'),
	{
		header: '',
		accessorKey: 'actions',
		enableSorting: false,
		th: { width: 110 },
		td: { align: 'center' },
		responsive: { sizeColumn: 'full' },
		cell: ({ row }) => (
			<CTNButton
				variant="ghost"
				mode="secondary"
				size="size-32"
				label="Ver detalle"
				onClick={() => onViewDetail?.(row.original)}
			/>
		),
	},
];

export { getColumnsAtendidas, getColumnsPendiente };